import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Trash2,
  AlertCircle,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  User,
} from "lucide-react";

export default function KelolaEvaluasi() {
  const [siswaList, setSiswaList] = useState([]);
  const [evaluasiList, setEvaluasiList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // UI STATE (Notifikasi & Modal)
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [deleteModal, setDeleteModal] = useState({ show: false, id: null });

  // Form Data
  const [formData, setFormData] = useState({
    siswa_id: "",
    tanggal: new Date().toISOString().split("T")[0],
    mapel: "",
    narasi: "",
  });

  useEffect(() => {
    const savedUser = JSON.parse(localStorage.getItem("ruangKelasUser"));
    if (savedUser) {
      setCurrentUser(savedUser);
      fetchData(savedUser);
    }
  }, []);

  // FUNGSI NOTIFIKASI (TOAST)
  const showToast = (message, type = "success") => {
    setNotification({ show: true, message, type });
    // Hilang otomatis setelah 3 detik
    setTimeout(
      () => setNotification((prev) => ({ ...prev, show: false })),
      3000
    );
  };

  async function fetchData(user) {
    setLoading(true);
    setErrorMsg("");
    let listSiswaFinal = [];

    try {
      if (user.role === "tutor") {
        const { data: dataAkun } = await supabase
          .from("akun")
          .select("id")
          .eq("username", user.username)
          .single();
        if (!dataAkun) throw new Error("Akun login tidak ditemukan.");

        const { data: dataTutor } = await supabase
          .from("tutor")
          .select("id")
          .eq("akun_id", dataAkun.id)
          .single();
        if (dataTutor) {
          const { data: relasi } = await supabase
            .from("relasi_siswa_tutor")
            .select("siswa(id, nama_lengkap, kelas)")
            .eq("tutor_id", dataTutor.id);
          listSiswaFinal = relasi?.map((r) => r.siswa) || [];
        }
      } else {
        const { data } = await supabase
          .from("siswa")
          .select("id, nama_lengkap, kelas")
          .order("nama_lengkap");
        listSiswaFinal = data || [];
      }

      setSiswaList(listSiswaFinal);

      const { data: dataNilai, error } = await supabase
        .from("nilai")
        .select("*, siswa(nama_lengkap)")
        .order("id", { ascending: false });
      if (error) throw error;
      setEvaluasiList(dataNilai || []);
    } catch (err) {
      console.error(err);
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.siswa_id || !formData.narasi)
      return showToast("Mohon pilih siswa dan isi narasi!", "error");

    try {
      const { error } = await supabase.from("nilai").insert([
        {
          siswa_id: formData.siswa_id,
          mata_pelajaran: formData.mapel,
          catatan: formData.narasi,
          tanggal: formData.tanggal,
          jenis_ujian: "Evaluasi Harian",
          skor: 0,
        },
      ]);

      if (error) throw error;

      showToast("Evaluasi berhasil disimpan!", "success");
      setFormData({ ...formData, mapel: "", narasi: "" });
      fetchData(currentUser);
    } catch (error) {
      showToast("Gagal menyimpan: " + error.message, "error");
    }
  }

  // Memicu Modal Konfirmasi
  function clickDelete(id) {
    setDeleteModal({ show: true, id: id });
  }

  // Eksekusi Hapus setelah konfirmasi
  async function confirmDelete() {
    try {
      const { error } = await supabase
        .from("nilai")
        .delete()
        .eq("id", deleteModal.id);
      if (error) throw error;

      showToast("Catatan evaluasi dihapus.", "success");
      fetchData(currentUser);
    } catch (error) {
      showToast("Gagal menghapus: " + error.message, "error");
    } finally {
      setDeleteModal({ show: false, id: null });
    }
  }

  const formatTglIndo = (str) => {
    if (!str) return "-";
    return new Date(str).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // --- STYLES ---
  const styles = {
    container: {
      padding: "20px",
      maxWidth: "800px",
      margin: "0 auto",
      fontFamily: "sans-serif",
      background: "#f8fafc",
      minHeight: "100vh",
    },
    card: {
      background: "white",
      padding: "25px",
      borderRadius: "16px",
      border: "1px solid #e2e8f0",
      marginBottom: "20px",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
    },
    input: {
      width: "100%",
      padding: "12px",
      marginBottom: "15px",
      borderRadius: "10px",
      border: "1px solid #cbd5e1",
      display: "block",
      boxSizing: "border-box",
      outline: "none",
      transition: "border 0.2s",
    },
    textarea: {
      width: "100%",
      padding: "12px",
      marginBottom: "15px",
      borderRadius: "10px",
      border: "1px solid #cbd5e1",
      minHeight: "100px",
      fontFamily: "sans-serif",
      boxSizing: "border-box",
      outline: "none",
    },
    btn: {
      background: "#2563eb",
      color: "white",
      padding: "12px 20px",
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      fontWeight: "bold",
      width: "100%",
      transition: "background 0.2s",
    },

    // Toast Notification Style
    toast: {
      position: "fixed",
      top: notification.show ? "30px" : "-80px",
      left: "50%",
      transform: "translateX(-50%)",
      background: "white",
      padding: "12px 24px",
      borderRadius: "50px",
      boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      zIndex: 9999,
      border:
        notification.type === "success"
          ? "1px solid #dcfce7"
          : "1px solid #fee2e2",
      transition: "all 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55)",
      opacity: notification.show ? 1 : 0,
    },

    // Modal Overlay Style
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(15, 23, 42, 0.6)",
      backdropFilter: "blur(4px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10000,
      opacity: deleteModal.show ? 1 : 0,
      visibility: deleteModal.show ? "visible" : "hidden",
      transition: "all 0.2s ease-in-out",
    },
    modalCard: {
      background: "white",
      padding: "30px",
      borderRadius: "20px",
      width: "90%",
      maxWidth: "350px",
      textAlign: "center",
      transform: deleteModal.show ? "scale(1)" : "scale(0.95)",
      transition: "all 0.2s",
    },
  };

  if (loading)
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader2 className="animate-spin" size={40} color="#2563eb" />
      </div>
    );

  return (
    <div style={styles.container}>
      {/* 1. TOAST NOTIFICATION (Melayang di Atas) */}
      <div style={styles.toast}>
        {notification.type === "success" ? (
          <CheckCircle size={20} color="#22c55e" />
        ) : (
          <AlertCircle size={20} color="#ef4444" />
        )}
        <span style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b" }}>
          {notification.message}
        </span>
      </div>

      {/* 2. MODAL KONFIRMASI HAPUS */}
      <div style={styles.modalOverlay}>
        <div style={styles.modalCard}>
          <div
            style={{
              width: 60,
              height: 60,
              background: "#FEF2F2",
              borderRadius: "50%",
              margin: "0 auto 20px auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Trash2 size={30} color="#EF4444" />
          </div>
          <h3 style={{ margin: "0 0 10px 0", color: "#1E293B" }}>
            Hapus Evaluasi?
          </h3>
          <p
            style={{
              color: "#64748B",
              fontSize: "14px",
              marginBottom: 25,
              lineHeight: "1.5",
            }}
          >
            Data evaluasi ini akan dihapus permanen dan tidak bisa dikembalikan.
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => setDeleteModal({ show: false, id: null })}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                background: "white",
                fontWeight: "bold",
                cursor: "pointer",
                color: "#64748b",
              }}
            >
              Batal
            </button>
            <button
              onClick={confirmDelete}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 10,
                border: "none",
                background: "#EF4444",
                color: "white",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Ya, Hapus
            </button>
          </div>
        </div>
      </div>

      <Link
        to="/"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          textDecoration: "none",
          color: "#64748b",
          marginBottom: "20px",
        }}
      >
        <ArrowLeft size={18} /> Kembali
      </Link>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h2 style={{ margin: 0, color: "#1e293b" }}>📝 Kelola Evaluasi</h2>
      </div>

      {/* 3. ALERT ERROR (Jika ada error database) */}
      {errorMsg && (
        <div
          style={{
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            color: "#991B1B",
            padding: "15px",
            borderRadius: "12px",
            marginBottom: "20px",
            display: "flex",
            gap: "12px",
            alignItems: "center",
          }}
        >
          <AlertCircle size={24} style={{ flexShrink: 0 }} />
          <div>
            <strong style={{ display: "block", fontSize: 14 }}>
              Terjadi Kesalahan
            </strong>
            <span style={{ fontSize: 13 }}>{errorMsg}</span>
          </div>
        </div>
      )}

      {/* FORM INPUT */}
      <div style={styles.card}>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 15 }}>
            <label
              style={{
                fontWeight: "700",
                display: "block",
                marginBottom: 8,
                color: "#64748b",
                fontSize: 11,
                textTransform: "uppercase",
              }}
            >
              Pilih Siswa
            </label>
            <div style={{ position: "relative" }}>
              <User
                size={18}
                style={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  color: "#94a3b8",
                }}
              />
              <select
                style={{ ...styles.input, paddingLeft: 40, marginBottom: 0 }}
                value={formData.siswa_id}
                onChange={(e) =>
                  setFormData({ ...formData, siswa_id: e.target.value })
                }
                disabled={siswaList.length === 0}
              >
                <option value="">
                  {siswaList.length > 0
                    ? "-- Pilih Siswa Binaan --"
                    : "-- Tidak ada siswa ditemukan --"}
                </option>
                {siswaList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nama_lengkap} ({s.kelas})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: "flex", gap: "15px", marginBottom: 15 }}>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  fontWeight: "700",
                  display: "block",
                  marginBottom: 8,
                  color: "#64748b",
                  fontSize: 11,
                  textTransform: "uppercase",
                }}
              >
                Tanggal
              </label>
              <div style={{ position: "relative" }}>
                <Calendar
                  size={18}
                  style={{
                    position: "absolute",
                    top: 12,
                    left: 12,
                    color: "#94a3b8",
                  }}
                />
                <input
                  type="date"
                  style={{ ...styles.input, paddingLeft: 40, marginBottom: 0 }}
                  value={formData.tanggal}
                  onChange={(e) =>
                    setFormData({ ...formData, tanggal: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  fontWeight: "700",
                  display: "block",
                  marginBottom: 8,
                  color: "#64748b",
                  fontSize: 11,
                  textTransform: "uppercase",
                }}
              >
                Topik / Mapel
              </label>
              <input
                style={{ ...styles.input, marginBottom: 0 }}
                placeholder="Contoh: Matematika"
                value={formData.mapel}
                onChange={(e) =>
                  setFormData({ ...formData, mapel: e.target.value })
                }
                required
              />
            </div>
          </div>

          <label
            style={{
              fontWeight: "700",
              display: "block",
              marginBottom: 8,
              color: "#64748b",
              fontSize: 11,
              textTransform: "uppercase",
            }}
          >
            Narasi Perkembangan
          </label>
          <textarea
            style={styles.textarea}
            placeholder="Tulis catatan perkembangan siswa di sini..."
            value={formData.narasi}
            onChange={(e) =>
              setFormData({ ...formData, narasi: e.target.value })
            }
            required
          />

          <button
            type="submit"
            style={styles.btn}
            disabled={siswaList.length === 0}
          >
            <Save size={18} /> Simpan Evaluasi
          </button>
        </form>
      </div>

      {/* LIST RIWAYAT */}
      <h3 style={{ color: "#334155", fontSize: 16, marginBottom: 15 }}>
        Riwayat Evaluasi Terakhir
      </h3>

      {evaluasiList.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            background: "white",
            borderRadius: "16px",
            border: "1px dashed #cbd5e1",
            color: "#94a3b8",
          }}
        >
          <AlertTriangle size={30} style={{ opacity: 0.5, marginBottom: 10 }} />
          <p style={{ margin: 0 }}>Belum ada data evaluasi.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 15 }}>
          {evaluasiList.map((item) => (
            <div
              key={item.id}
              style={{
                background: "white",
                padding: "20px",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
              }}
            >
              <div>
                <div
                  style={{
                    fontWeight: "700",
                    color: "#1e293b",
                    fontSize: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {item.siswa?.nama_lengkap || "Siswa Dihapus"}
                  <span
                    style={{
                      fontSize: 10,
                      background: "#e0f2fe",
                      color: "#0284c7",
                      padding: "2px 8px",
                      borderRadius: 10,
                    }}
                  >
                    {item.mata_pelajaran}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#64748b",
                    margin: "4px 0 8px 0",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <Calendar size={12} /> {formatTglIndo(item.tanggal)}
                </div>
                <p
                  style={{
                    margin: 0,
                    color: "#334155",
                    fontSize: 14,
                    lineHeight: "1.6",
                    background: "#f8fafc",
                    padding: 10,
                    borderRadius: 8,
                  }}
                >
                  {item.catatan}
                </p>
              </div>
              <button
                onClick={() => clickDelete(item.id)}
                style={{
                  border: "none",
                  background: "#fee2e2",
                  color: "#ef4444",
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "8px",
                  marginLeft: 10,
                  transition: "0.2s",
                }}
                title="Hapus"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
